import type { MapAdapter, MapInitOptions, MarkerSetupOptions, LatLng } from './MapAdapter';

declare const kakao: any;

export class KakaoMapAdapter implements MapAdapter {
    private map: any = null;
    private markers: Record<string, any> = {};
    private infoWindows: Record<string, any> = {};
    private pinOptions: Record<string, { url: string; w: number; h: number }> = {};
    private markerLatLng: Record<string, any> = {};
    private ripples: Record<string, any> = {};

    async init(options: MapInitOptions): Promise<void> {
        return new Promise<void>((resolve) => {
            const { container, center, zoom } = options;
            kakao.maps.load(() => {
                // Kakao level is inverse of zoom: lower level = more zoomed in.
                // Naver zoom 14 ≈ Kakao level 3.
                const level = Math.max(1, Math.round(17 - zoom));
                this.map = new kakao.maps.Map(container, {
                    center: new kakao.maps.LatLng(center.lat, center.lng),
                    level,
                });
                resolve();
            });
        });
    }

    addMarker(id: string, options: MarkerSetupOptions): void {
        const { position, title, pin } = options;
        const latlng = new kakao.maps.LatLng(position.lat, position.lng);
        this.markerLatLng[id] = latlng;
        const markerOptions: any = { position: latlng, map: this.map, title };
        if (pin) {
            this.pinOptions[id] = { url: pin.url, w: pin.width, h: pin.height };
            markerOptions.image = this.buildImage(pin.url, pin.width, pin.height, false);
        }
        this.markers[id] = new kakao.maps.Marker(markerOptions);
    }

    setMarkerPosition(id: string, position: LatLng): void {
        const latlng = new kakao.maps.LatLng(position.lat, position.lng);
        this.markerLatLng[id] = latlng;
        if (this.markers[id]) {
            this.markers[id].setPosition(latlng);
        }
        if (this.ripples[id]) {
            this.ripples[id].setMap(null);
            delete this.ripples[id];
        }
    }

    private buildImage(url: string, w: number, h: number, active: boolean): any {
        const scale = active ? 1.18 : 1;
        const sw = Math.round(w * scale);
        const sh = Math.round(h * scale);
        const size = new kakao.maps.Size(sw, sh);
        const offset = new kakao.maps.Point(sw / 2, sh);
        return new kakao.maps.MarkerImage(url, size, { offset });
    }

    setMarkerActive(id: string, active: boolean): void {
        const pin = this.pinOptions[id];
        if (!pin || !this.markers[id]) return;
        this.markers[id].setImage(this.buildImage(pin.url, pin.w, pin.h, active));
        this.markers[id].setZIndex(active ? 10 : 1);
    }

    setInfoWindowContent(id: string, html: string): void {
        this.infoWindows[id] = new kakao.maps.InfoWindow({ content: html });
    }

    openInfoWindow(id: string): void {
        if (this.infoWindows[id] && this.markers[id]) {
            this.infoWindows[id].open(this.map, this.markers[id]);
        }
    }

    closeAllInfoWindows(): void {
        Object.values(this.infoWindows).forEach((w) => w.close());
    }

    setMarkerVisible(id: string, visible: boolean): void {
        if (this.markers[id]) {
            this.markers[id].setMap(visible ? this.map : null);
        }
    }

    setRipple(id: string, show: boolean): void {
        if (!show) {
            if (this.ripples[id]) {
                this.ripples[id].setMap(null);
                delete this.ripples[id];
            }
            return;
        }
        if (this.ripples[id]) return;
        const pos = this.markerLatLng[id];
        if (!pos || !this.map) return;

        const el = document.createElement('div');
        el.className = 'store-pin-ripple';

        const overlay = new kakao.maps.CustomOverlay({
            position: pos,
            content: el,
            xAnchor: 0.5,
            yAnchor: 0.5,
            zIndex: 1,
        });
        overlay.setMap(this.map);
        this.ripples[id] = overlay;
    }

    onMarkerClick(id: string, callback: () => void): void {
        if (this.markers[id]) {
            kakao.maps.event.addListener(this.markers[id], 'click', callback);
        }
    }

    panTo(pos: LatLng): void {
        if (this.map) this.map.panTo(new kakao.maps.LatLng(pos.lat, pos.lng));
    }

    setZoom(zoom: number): void {
        if (this.map) this.map.setLevel(Math.max(1, Math.round(17 - zoom)));
    }

    getZoom(): number {
        return this.map ? 17 - (this.map.getLevel() as number) : 0;
    }

    fitBounds(positions: LatLng[], _padding = 60): void {
        if (!this.map || positions.length === 0) return;
        if (positions.length === 1) {
            this.panTo(positions[0]);
            this.setZoom(15);
            return;
        }
        const bounds = new kakao.maps.LatLngBounds();
        positions.forEach((p) => bounds.extend(new kakao.maps.LatLng(p.lat, p.lng)));
        this.map.setBounds(bounds);
    }
}
